import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Header, Footer, Seo } from "../../components";
import { PaginationNav } from "../../components/PaginationNav";
import { column } from "../../store";
import { OG_IMAGE, canonicalUrl } from "../../utils/seo";
import searchIcon from "../../icons/search.svg";
import { FaqHero } from "./FaqHero";
import { FaqListItem } from "./FaqListItem";
import { FAQ_CATEGORIES } from "./faqCategories";
import "./Category/FaqCategory.css";
import {
  FAQ_FETCH_SORT,
  FAQ_POSTS_PER_PAGE,
  FAQ_SEARCH_DEBOUNCE_MS,
} from "./faqPageConfig";
import { mapColumnToFaqItem } from "./faqPageUtils";
import "./style.css";

export const FAQ = () => {
  const [currentPage, setCurrentPage] = useState(1);
  // searchInput: 입력창 즉시 값 / searchKeyword: 디바운스 후 서버로 보내는 값
  const [searchInput, setSearchInput] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  const { columns, totalCount, isLoading, getColumns } = column((state) => ({
    columns: state.columns,
    totalCount: state.totalCount,
    isLoading: state.isLoading,
    getColumns: state.getColumns,
  }));

  // 검색어 디바운스: 입력이 멈추면 서버 검색어를 갱신하고 1페이지로 이동
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchKeyword(searchInput);
      setCurrentPage(1);
    }, FAQ_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // 목록 조회: 서버 페이지네이션 + 서버 검색
  useEffect(() => {
    const skip = (currentPage - 1) * FAQ_POSTS_PER_PAGE;
    getColumns(skip, FAQ_POSTS_PER_PAGE, FAQ_FETCH_SORT, searchKeyword.trim());
  }, [currentPage, searchKeyword, getColumns]);

  // 현재 페이지 항목(서버가 페이지 단위로 반환)
  const faqItems = useMemo(
    () => (columns ?? []).map(mapColumnToFaqItem),
    [columns]
  );

  // 총 페이지는 검색이 반영된 서버 total_count 기준
  const totalPages = Math.ceil(totalCount / FAQ_POSTS_PER_PAGE) || 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    document
      .querySelector(".faq-board-page__content")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <>
      <Seo
        title="양형교육 자주 묻는 질문｜희망과학 심리상담센터"
        description="온라인 양형교육과 재범방지교육의 신청 방법, 교육비, 수강시간, 수료 조건 및 수료증 발급에 관한 자주 묻는 질문을 확인하세요."
        ogTitle="양형교육 자주 묻는 질문｜희망과학"
        ogDescription="교육 신청부터 온라인 수강, 수료 조건과 수료증 발급까지 자주 묻는 질문을 확인하세요."
        image={OG_IMAGE}
        canonical={canonicalUrl("/faq")}
      />
      <div className="faq-board-page-shell">
        <Header variant="dark" />

        <main className="faq-board-page">
          <FaqHero />

          <section
            className="faq-board-page__content"
            aria-label="자주 묻는 질문 목록"
          >
            <div className="faq-board-page__inner">
              <div className="faq-board-toolbar">
                <form
                  className="faq-board-toolbar__search"
                  role="search"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <img
                    src={searchIcon}
                    alt=""
                    className="faq-board-toolbar__search-icon"
                  />
                  <input
                    type="search"
                    className="faq-board-toolbar__search-input"
                    placeholder="질문·내용 검색"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    aria-label="자주 묻는 질문 검색"
                  />
                </form>

                {/* 분야별(카테고리) 바로가기 — 기존 해시태그 필터 위치 */}
                <nav
                  className="faq-board-toolbar__categories"
                  aria-label="법률정보 카테고리"
                >
                  <Link
                    to="/faq"
                    className="faq-category-nav__chip faq-category-nav__chip--active"
                    aria-current="page"
                  >
                    전체
                  </Link>
                  {FAQ_CATEGORIES.map((c) => (
                    <Link
                      key={c.slug}
                      to={`/faq/category/${c.slug}`}
                      className="faq-category-nav__chip"
                    >
                      {c.navLabel}
                    </Link>
                  ))}
                </nav>
              </div>

              <p className="faq-board-page__count">
                총 {totalCount.toLocaleString()}건
              </p>

              <div className="faq-board-list">
                {isLoading ? (
                  // 로딩 중에도 목록과 동일한 높이의 스켈레톤을 렌더해
                  // 데이터 도착 시 레이아웃이 튀지 않게 한다.
                  <div
                    className="faq-board-list__skeleton"
                    role="status"
                    aria-label="불러오는 중"
                  >
                    {Array.from({ length: FAQ_POSTS_PER_PAGE }).map((_, i) => (
                      <div className="faq-board-skeleton__row" key={i}>
                        <span className="faq-board-skeleton__bar faq-board-skeleton__bar--title" />
                        <span className="faq-board-skeleton__bar faq-board-skeleton__bar--tag" />
                        <span className="faq-board-skeleton__bar faq-board-skeleton__bar--date" />
                      </div>
                    ))}
                  </div>
                ) : faqItems.length > 0 ? (
                  faqItems.map((item) => (
                    <FaqListItem key={item.id} item={item} />
                  ))
                ) : (
                  <p className="faq-board-page__empty" role="status">
                    {searchKeyword.trim()
                      ? "조건에 해당하는 질문이 없습니다."
                      : "등록된 질문이 없습니다."}
                  </p>
                )}
              </div>

              {!isLoading && faqItems.length > 0 && (
                <PaginationNav
                  className="faq-board-page__pagination"
                  ariaLabel="자주 묻는 질문 페이지"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </div>
          </section>
        </main>
      </div>

      <Footer />
    </>
  );
};
